import React, { useMemo } from 'react';
import { colors } from '../../../constants/colors';
import {
  eventColor,
  eventIcon,
  formatEventLabel,
} from '../../../constants/webAnalyticsTheme';

const EventBreakdownList = ({ data = [], isLoading = false }) => {
  const rows = useMemo(
    () => [...(data || [])].sort((a, b) => (b.count || 0) - (a.count || 0)),
    [data]
  );

  if (isLoading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        <style>
          {`
            @keyframes wa-ev-skel {
              0% { opacity: 0.55; }
              50% { opacity: 1; }
              100% { opacity: 0.55; }
            }
          `}
        </style>
        {[0, 1, 2, 3].map((i) => (
          <div key={i}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: 6,
              }}
            >
              <div
                style={{
                  height: 12,
                  width: '45%',
                  borderRadius: 4,
                  backgroundColor: colors.background.tertiary,
                  animation: 'wa-ev-skel 1.2s ease-in-out infinite',
                }}
              />
              <div
                style={{
                  height: 12,
                  width: 36,
                  borderRadius: 4,
                  backgroundColor: colors.background.tertiary,
                  animation: 'wa-ev-skel 1.2s ease-in-out infinite',
                }}
              />
            </div>
            <div
              style={{
                height: 6,
                width: '100%',
                borderRadius: 999,
                backgroundColor: colors.background.tertiary,
                animation: 'wa-ev-skel 1.2s ease-in-out infinite',
              }}
            />
          </div>
        ))}
      </div>
    );
  }

  if (!rows.length) {
    return (
      <div
        style={{
          padding: '40px 16px',
          textAlign: 'center',
          color: colors.text.secondary,
        }}
      >
        <p style={{ margin: 0, fontWeight: 600, fontSize: '15px' }}>
          No events yet
        </p>
        <p style={{ margin: '6px 0 0', fontSize: '13px' }}>
          Event types will show once analytics starts collecting
        </p>
      </div>
    );
  }

  const max = Math.max(...rows.map((d) => d.count || 0), 1);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      {rows.map((row) => {
        const count = row.count || 0;
        const pct =
          row.percentage != null
            ? row.percentage
            : Math.round((count / max) * 1000) / 10;
        const widthPct = Math.max(6, Math.round((count / max) * 100));
        const label = formatEventLabel(row.event);
        const barColor = eventColor(row.event);
        return (
          <div key={row.event}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                gap: '12px',
                marginBottom: '6px',
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontSize: '13px',
                    fontWeight: 600,
                    color: colors.text.primary,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <span aria-hidden="true" style={{ lineHeight: 1 }}>
                    {eventIcon(row.event)}
                  </span>
                  {label}
                </div>
                <div
                  style={{
                    fontSize: '12px',
                    fontWeight: 600,
                    color: barColor,
                    marginTop: 2,
                  }}
                >
                  {pct}%
                </div>
              </div>
              <span
                style={{
                  fontSize: '12px',
                  color: colors.text.secondary,
                  flexShrink: 0,
                }}
              >
                {count.toLocaleString()}
              </span>
            </div>
            <div
              style={{
                height: '6px',
                borderRadius: '999px',
                backgroundColor: colors.background.tertiary,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${widthPct}%`,
                  height: '100%',
                  borderRadius: '999px',
                  backgroundColor: barColor,
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default EventBreakdownList;
